import {brokerSession} from "../session/brokerSession";

export const API_BASE = `${process.env.REACT_APP_API_BASE}/api`;

/** An API error with the HTTP status, so screens can tell "log in again" (401) from "not allowed" (403). */
export class ApiError extends Error {
    constructor(message, status) {
        super(message);
        this.status = status;
    }
}

const FALLBACK = {
    400: "Please check the details and try again.",
    401: "Please log in again.",
    403: "You're not allowed to do that.",
    404: "Not found.",
    409: "That can't be done right now.",
    413: "That file is too large.",
    429: "Too many attempts. Please wait a little and try again.",
};

/**
 * fetch() against the backend. `auth` is "broker" (the broker session), a token getter, or null; `body` objects are
 * sent as JSON and FormData as-is. A 401 on a broker call ends the broker session.
 */
export async function request(path, {method = "GET", body, auth = "broker"} = {}) {
    const headers = {};
    const token = auth === "broker" ? brokerSession.get() : typeof auth === "function" ? auth() : null;
    if (token) headers.Authorization = `Bearer ${token}`;
    let payload = body;
    if (body !== undefined && !(body instanceof FormData)) {
        headers["Content-Type"] = "application/json";
        payload = JSON.stringify(body);
    }

    let res;
    try {
        res = await fetch(`${API_BASE}${path}`, {method, headers, body: payload});
    } catch {
        throw new ApiError("Can't reach the server. Check your connection and try again.", 0);
    }
    if (res.status === 204) return null;
    const text = await res.text();
    let data = null;
    try { data = text ? JSON.parse(text) : null; } catch { data = text; }

    if (!res.ok) {
        if (res.status === 401 && auth === "broker") brokerSession.expire();
        const message = (data && (data.error || data.detail)) || FALLBACK[res.status] || "Something went wrong. Please try again.";
        throw new ApiError(typeof message === "string" ? message : FALLBACK[res.status], res.status);
    }
    return data;
}
